import { Icon } from '@foam/buoy/src/components/Icon/Icon';
import { cn } from '@foam/buoy/src/lib/utils';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

interface CarouselProps {
  children: ReactNode;
  containerClassName?: string;
  className?: string;
  leftNavigationClassName?: string;
  rightNavigationClassName?: string;
  showNavigation?: boolean;
  align?: 'left' | 'center';
}

export const Carousel = ({
  children,
  containerClassName = '',
  className = '',
  leftNavigationClassName = '',
  rightNavigationClassName = '',
  showNavigation = true,
  align = 'center',
}: CarouselProps) => {
  const ref = useRef<HTMLDivElement>(null!);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollPosition = useCallback(() => {
    if (ref.current) {
      const { scrollLeft, scrollWidth, clientWidth } = ref.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  useEffect(() => {
    checkScrollPosition();
  }, [checkScrollPosition]);

  const next = useCallback(() => {
    if (ref.current) {
      const nextPosition = ref.current.scrollLeft + 200;
      ref.current.scrollTo({ top: 0, left: nextPosition, behavior: 'smooth' });
    }
  }, []);

  const prev = useCallback(() => {
    if (ref.current) {
      const prevPosition = ref.current.scrollLeft - 200;
      ref.current.scrollTo({ top: 0, left: prevPosition, behavior: 'smooth' });
    }
  }, []);

  const handleScroll = useCallback(() => {
    checkScrollPosition();
  }, [checkScrollPosition]);

  return (
    <div className={cn('group relative', className)}>
      {/* Navigation Buttons for the Carousel */}
      {showNavigation && (
        <>
          {/* Previous Button */}
          {canScrollLeft && (
            <button
              onClick={prev}
              className={cn(
                'invisible absolute left-2 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center transition-all bg-neutral-50 group-hover:visible',
                leftNavigationClassName,
              )}
              aria-label="Previous items"
              style={{
                width: '32px',
                height: '32px',
                backgroundColor: 'white',
                borderRadius: '50%',
                boxShadow:
                  '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              }}
            >
              <Icon name="ChevronLeft" />
            </button>
          )}

          {/* Next Button */}
          {canScrollRight && (
            <button
              onClick={next}
              className={cn(
                'absolute right-2 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center transition-all bg-neutral-50',
                rightNavigationClassName,
              )}
              aria-label="Next items"
              style={{
                width: '32px',
                height: '32px',
                backgroundColor: 'white',
                borderRadius: '50%',
                boxShadow:
                  '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              }}
            >
              <Icon name="ChevronRight" />
            </button>
          )}
        </>
      )}

      {/* Scrollable Container */}
      <div
        ref={ref}
        className={cn('relative overflow-x-auto', containerClassName)}
        onScroll={handleScroll}
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        <div
          className={cn('flex', {
            'mx-auto w-max min-w-full justify-center gap-4': align === 'center',
            'gap-2': align === 'left',
          })}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
